import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ScopeProvider, useScope } from './context/ScopeContext';
import { CallWindowsProvider } from './context/CallWindowsContext';
import GoogleLogin from './components/Auth/GoogleLogin';
import Sidebar from './components/Layout/Sidebar';
import { AssistantPane, AssistantProvider } from './components/AssistantDock';
import CommandPalette from './components/CommandPalette';
import TodayPage from './pages/TodayPage';
import QueuePage from './pages/QueuePage';
import VisitsPage from './pages/VisitsPage';
import LeadList from './pages/LeadList';
import LeadDetail from './pages/LeadDetail';
import CallsPage from './pages/CallsPage';
import MessagesPage from './pages/MessagesPage';
import DashboardPage from './pages/DashboardPage';
import UsersPage from './pages/UsersPage';
import TrainingPage from './pages/TrainingPage';
import SettingsPage from './pages/SettingsPage';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  // Carry the intended destination (incl. query string) through the login
  // flow so deep links like /leads?tag=… survive an auth redirect.
  return isAuthenticated
    ? <>{children}</>
    : <Navigate to="/login" state={{ from: location }} replace />;
};

/** Admin-only screens. A UI gate — core's API key still authorises the calls. */
const AdminOnly: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  return user?.role === 'admin' ? <>{children}</> : <Navigate to="/today" replace />;
};

const LoginRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string; search: string } } | null)?.from;
  const dest = from ? `${from.pathname}${from.search}` : '/today';
  return isAuthenticated ? <Navigate to={dest} replace /> : <GoogleLogin />;
};

/** The signed-in chrome: sidebar + screen + the ⌘K palette that spans both.
 *  The screen is keyed by the scope pick: changing whose book you view
 *  remounts it, so every page refetches through the header the API clients
 *  attach — no page knows about the scope. */
const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [jumping, setJumping] = useState(false);
  const { pick } = useScope();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setJumping(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div className="flex h-screen">
      <Sidebar onJump={() => setJumping(true)} />
      <AssistantPane><React.Fragment key={pick ?? 'all'}>{children}</React.Fragment></AssistantPane>
      <CommandPalette open={jumping} onClose={() => setJumping(false)} />
    </div>
  );
};

const guarded = (el: React.ReactNode) => (
  <ProtectedRoute><Shell>{el}</Shell></ProtectedRoute>
);

const AppContent: React.FC = () => (
  <Router>
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route path="/today" element={guarded(<TodayPage />)} />
      <Route path="/calls" element={guarded(<CallsPage />)} />
      <Route path="/messages" element={guarded(<MessagesPage />)} />
      <Route path="/leads" element={guarded(<LeadList />)} />
      <Route path="/leads/:leadId" element={guarded(<LeadDetail />)} />
      <Route path="/dashboard" element={guarded(<DashboardPage />)} />
      <Route path="/queue" element={guarded(<QueuePage />)} />
      <Route path="/visits" element={guarded(<VisitsPage />)} />
      <Route path="/users" element={guarded(<AdminOnly><UsersPage /></AdminOnly>)} />
      <Route path="/training" element={guarded(<TrainingPage />)} />
      <Route path="/settings" element={guarded(<SettingsPage />)} />
      <Route path="/chats" element={<Navigate to="/messages" replace />} />
      <Route path="/tags" element={<Navigate to="/dashboard" replace />} />
      <Route path="/" element={<Navigate to="/today" replace />} />
    </Routes>
  </Router>
);

function App() {
  return (
    <AuthProvider>
      <ScopeProvider>
        <AssistantProvider>
          <CallWindowsProvider>
            <AppContent />
          </CallWindowsProvider>
        </AssistantProvider>
      </ScopeProvider>
    </AuthProvider>
  );
}

export default App;
